import React, { useState } from 'react';

interface AuditLogEntry {
  id: string;
  eventType: string;
  description: string;
  staffName: string;
  timestamp: Date;
  severity: 'info' | 'warning' | 'critical';
}

interface AuditLogViewerProps {
  entries: AuditLogEntry[];
  onSearch: (query: string) => void;
  isLoading?: boolean;
}

export function AuditLogViewer({ entries, onSearch, isLoading }: AuditLogViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');

  const filteredEntries = entries.filter((entry) => {
    const matchesFilter = filter === 'all' || entry.severity === filter;
    const matchesSearch =
      !searchQuery ||
      entry.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.staffName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.eventType.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch(query);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-bold text-gray-900 mb-4">Audit Log</h2>

      <div className="mb-4 flex gap-4">
        <input
          type="text"
          placeholder="Search by event, staff, or type..."
          value={searchQuery}
          onChange={handleSearch}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Severity</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading audit log...</p>
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No audit log entries found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left p-3 font-semibold">Timestamp</th>
                <th className="text-left p-3 font-semibold">Event</th>
                <th className="text-left p-3 font-semibold">Description</th>
                <th className="text-left p-3 font-semibold">Staff</th>
                <th className="text-center p-3 font-semibold">Severity</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 text-gray-600 text-xs">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="p-3 font-mono text-xs text-gray-700">{entry.eventType}</td>
                  <td className="p-3 text-gray-700">{entry.description}</td>
                  <td className="p-3 text-gray-600">{entry.staffName}</td>
                  <td className="p-3 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(entry.severity)}`}>
                      {entry.severity.charAt(0).toUpperCase() + entry.severity.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Showing {filteredEntries.length} of {entries.length} entries. Audit log is searchable by event type, staff name, or description.
      </div>
    </div>
  );
}
